import AutoCompleteAddress from "./auto-complete-address";
import { useEffect, useState } from "react";
import { filters as defaultFilters, formatFilters } from "./utils";
import Input from "../inputs";
import { cloneDeep, find, get, identity } from "lodash";

import API_CONSTRUCTOR from "../../models/api-model";
import PropertyAPIs from "../../models/property";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ChevronLeft, ChevronRight, Filter, X } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { applyListingPriceToProperty } from "../../utils/property-calculations";
const apiModel = API_CONSTRUCTOR();
const propertyApi = new PropertyAPIs(apiModel);

const PAGE_SIZE = 25;
const MAP_BATCH_LIMIT = 500;

export const PropertySearch = ({
  onChange = identity,
  onPropertySelectFromSearch,
  setIsLoadingProperties = identity,
  onTotalCountChange = identity,
  onMapPropertiesChange = identity,
  setIsLoadingMapProperties = identity,
  externalTarget = null,
  onExternalTargetApplied = identity,
}) => {
  const [openFilter, setOpenFilter] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [error, setError] = useState(null);

  // Applied filters - these trigger the API call
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  // Draft filters - these are being edited in the modal
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [address, setAddress] = useState(null);
  const navigate = useNavigate();

  const handleAddressChange = (address) => {
    setAddress(address);
    setError(null); // Clear errors when address changes
  };

  const handleRetry = () => {
    setError(null);
    // Trigger a re-fetch by updating the address state
    if (address) {
      setAddress({ ...address });
    }
  };

  const handleFilterInput = (key) => (event) => {
    const clonedFilters = cloneDeep(draftFilters);
    const filter = find(clonedFilters, { key });
    const value = get(event, ["target", "value"], undefined);
    filter.value = value;

    setDraftFilters(clonedFilters);
  };

  const handleClearDraftFilters = () => {
    const newFilters = cloneDeep(defaultFilters);
    setDraftFilters(newFilters);
  };

  const handleClearAppliedFilters = () => {
    const newFilters = cloneDeep(defaultFilters);
    setAppliedFilters(newFilters);
    setDraftFilters(newFilters);
    setPageNumber(1);
    setError(null); // Clear errors when clearing filters
  };

  const handleApplyFilters = () => {
    setAppliedFilters(cloneDeep(draftFilters));
    setPageNumber(1);
    setOpenFilter(false);
    setError(null); // Clear errors when applying new filters
  };

  const handleOpenFilterModal = () => {
    // Sync draft filters with applied filters when opening modal
    setDraftFilters(cloneDeep(appliedFilters));
    setOpenFilter(true);
  };

  const handleCancelFilters = () => {
    // Discard draft changes and close modal
    setDraftFilters(cloneDeep(appliedFilters));
    setOpenFilter(false);
  };

  const handlePagination = (_, pageNumber) => {
    setPageNumber(pageNumber);
  };

  // Reset to page 1 when applied filters change
  useEffect(() => {
    if (address && !address.propertyId) {
      setPageNumber(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // Map "Search this area" or parent-driven target (same as picking a suggestion)
  useEffect(() => {
    if (!externalTarget) return;
    setAddress({ ...externalTarget });
    setPageNumber(1);
    setError(null);
    onExternalTargetApplied(externalTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTarget]);

  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!address) return;

      const { propertyId = "" } = address;

      if (propertyId) {
        if (onPropertySelectFromSearch) {
          setError(null);
          try {
            const result = await propertyApi.getPropertyDetails(propertyId);
            const property = get(result, "property", null);
            const meta = get(result, "meta", {});
            const { status } = meta;

            if (status === 200 && property) {
              applyListingPriceToProperty(property);
              onPropertySelectFromSearch(property);
              setError(null);
            } else {
              const errorMessage = get(
                meta,
                "message",
                "Property not found or unavailable"
              );
              setError(errorMessage);
              onPropertySelectFromSearch({ error: errorMessage });
              console.error(
                "Property details fetch failed:",
                status,
                errorMessage
              );
            }
          } catch (error) {
            const errorMessage =
              error?.message ||
              "Error fetching details for the property. Please try again.";

            setError(errorMessage);
            onPropertySelectFromSearch({ error: errorMessage });
            console.error("Error fetching property details:", error);
          }
        } else {
          navigate(`/property/${propertyId}`);
        }
      } else {
        setIsLoadingProperties(true);
        setError(null);
        const formattedFilters = formatFilters(appliedFilters);
        setTotalPage(0);

        try {
          const result = await propertyApi.getProperties({
            filter: formattedFilters,
            target: address,
            options: { offset: PAGE_SIZE * (pageNumber - 1), limit: PAGE_SIZE },
          });

          const properties = get(result, ["properties"], []);
          const meta = get(result, "meta", {});
          const { status } = meta;

          // Handle API response
          if (status === 200) {
            const { resultCount = 0 } = meta;
            const totalPage = Math.ceil(resultCount / PAGE_SIZE);
            setTotalPage(totalPage);
            setTotalResults(resultCount);
            onTotalCountChange(resultCount);
            onChange(properties);
            setError(null);
          } else {
            // Handle non-200 status responses
            const errorMessage = get(
              meta,
              "message",
              "Failed to fetch properties"
            );
            setError(errorMessage);
            onChange([]);
            setTotalPage(0);
            setTotalResults(0);
            onTotalCountChange(0);
            console.error("API returned non-200 status:", status, errorMessage);
          }
        } catch (error) {
          // Handle network errors or unexpected errors
          const errorMessage =
            error?.message ||
            "Unable to fetch properties. Please check your connection and try again.";

          setError(errorMessage);
          onChange([]);
          setTotalPage(0);
          setTotalResults(0);
          onTotalCountChange(0);
          console.error("Error fetching properties:", error);
        } finally {
          setIsLoadingProperties(false);
        }
      }
    };

    fetchPropertyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, pageNumber, appliedFilters]);

  // Bulk fetch for search-page map (same target + filters, not paginated)
  useEffect(() => {
    if (!address || address.propertyId) {
      onMapPropertiesChange([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoadingMapProperties(true);
      try {
        const formattedFilters = formatFilters(appliedFilters);
        const result = await propertyApi.getProperties({
          filter: formattedFilters,
          target: address,
          options: { offset: 0, limit: MAP_BATCH_LIMIT },
        });
        if (cancelled) return;
        const props = get(result, ["properties"], []);
        const meta = get(result, "meta", {});
        if (meta.status === 200) {
          onMapPropertiesChange(
            props.filter((p) => p.latitude && p.longitude)
          );
        } else {
          onMapPropertiesChange([]);
        }
      } catch (e) {
        if (!cancelled) onMapPropertiesChange([]);
      } finally {
        if (!cancelled) setIsLoadingMapProperties(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, appliedFilters]);

  // Count active applied filters
  const activeFiltersCount = appliedFilters.filter(
    (f) => f.value && f.value !== ""
  ).length;

  // Count draft filters (for modal display)
  const draftFiltersCount = draftFilters.filter(
    (f) => f.value && f.value !== ""
  ).length;

  // Quick filter chips
  const quickFilters = [
    { key: "pool", label: "Has Pool", icon: "💧", value: true },
    { key: "belowMarketPrice", label: "Below Market", icon: "📉", value: true },
    { key: "bankruptcy", label: "Bankruptcy", icon: "⚖️", value: true },
    { key: "cashBuyer", label: "Cash Buyer", icon: "💵", value: true },
  ];

  const handleQuickFilter = (filterKey, filterValue) => {
    const clonedFilters = cloneDeep(appliedFilters);
    const filter = find(clonedFilters, { key: filterKey });
    if (filter) {
      // Toggle: if already set, remove it; otherwise set it
      filter.value = filter.value === filterValue ? undefined : filterValue;
      setAppliedFilters(clonedFilters);
      // Also update draft filters to keep them in sync
      setDraftFilters(cloneDeep(clonedFilters));
      setError(null); // Clear errors when quick filter changes
    }
  };

  const isQuickFilterActive = (filterKey, filterValue) => {
    const filter = find(appliedFilters, { key: filterKey });
    return filter && filter.value === filterValue;
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Search Section */}
      <Card className="shadow-xl border-2">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex-1">
              <AutoCompleteAddress
                onPropertySelectFromSearch={onPropertySelectFromSearch}
                onChange={handleAddressChange}
              />
            </div>
            <Button
              onClick={handleOpenFilterModal}
              variant="outline"
              size="lg"
              className="relative min-w-[140px] border-2 hover:border-primary hover:bg-primary/5"
            >
              <Filter className="h-4 w-4 mr-2" />
              Advanced Filters
              {activeFiltersCount > 0 && (
                <Badge
                  variant="destructive"
                  className="ml-2 h-5 w-5 p-0 flex items-center justify-center rounded-full animate-pulse"
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </div>

          {/* Quick Filter Chips */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-muted-foreground mb-2 font-medium">
              Quick Filters:
            </p>
            <div className="flex flex-wrap gap-2">
              {quickFilters.map((qf) => (
                <Badge
                  key={qf.key}
                  variant={
                    isQuickFilterActive(qf.key, qf.value)
                      ? "default"
                      : "outline"
                  }
                  className={`cursor-pointer px-3 py-1.5 transition-all hover:scale-105 ${
                    isQuickFilterActive(qf.key, qf.value)
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => handleQuickFilter(qf.key, qf.value)}
                >
                  <span className="mr-1">{qf.icon}</span>
                  {qf.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Active Filters Display */}
          {activeFiltersCount > 0 && (
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-2 items-center">
              <span className="text-xs text-muted-foreground font-semibold">
                Active Filters ({activeFiltersCount}):
              </span>
              {appliedFilters
                .filter(
                  (f) =>
                    f.value !== undefined && f.value !== null && f.value !== ""
                )
                .map((filter) => {
                  // Format value based on filterType
                  let displayValue = String(filter.value);

                  switch (filter.filterType) {
                    case "boolean":
                      displayValue = filter.value ? "Yes" : "No";
                      break;

                    case "date":
                      if (typeof filter.value === "number") {
                        const date = new Date(filter.value);
                        displayValue = date.toLocaleDateString();
                      }
                      break;

                    case "currency":
                      displayValue = new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(filter.value);

                      break;

                    case "percentage":
                      if (typeof filter.value === "number") {
                        displayValue = `${filter.value}%`;
                      }
                      break;

                    case "number":
                      if (typeof filter.value === "number") {
                        displayValue = filter.value.toLocaleString();
                      }
                      break;

                    default:
                      displayValue = String(filter.value);
                  }

                  return (
                    <Badge
                      key={filter.key}
                      variant="secondary"
                      className="px-3 py-1 bg-blue-100 text-blue-800 border border-blue-200"
                    >
                      {filter.label}: {displayValue}
                    </Badge>
                  );
                })}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAppliedFilters}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-3"
              >
                <X className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Card className="mt-6 border-2 border-muted bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-center gap-3">
              <p className="text-sm text-muted-foreground text-center">
                No properties found
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError(null)}
                className="flex-shrink-0 h-8 w-8 p-0 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPage > 0 && (
        <Card className="mb-6 shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Showing page {pageNumber} of {totalPage} (
                {totalResults.toLocaleString()} properties)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handlePagination(null, Math.max(1, pageNumber - 1))
                  }
                  disabled={pageNumber === 1}
                  className="min-w-[100px]"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>

                {/* Page Numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {[...Array(Math.min(5, totalPage))].map((_, idx) => {
                    let pageNum;
                    if (totalPage <= 5) {
                      pageNum = idx + 1;
                    } else if (pageNumber <= 3) {
                      pageNum = idx + 1;
                    } else if (pageNumber >= totalPage - 2) {
                      pageNum = totalPage - 4 + idx;
                    } else {
                      pageNum = pageNumber - 2 + idx;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={pageNumber === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePagination(null, pageNum)}
                        className="w-10 h-10 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handlePagination(null, Math.min(totalPage, pageNumber + 1))
                  }
                  disabled={pageNumber === totalPage}
                  className="min-w-[100px]"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters Dialog */}
      <Dialog open={openFilter} onOpenChange={setOpenFilter}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4 bg-gradient-to-r from-primary/5 to-orange-500/5 -m-6 mb-0 p-6">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-orange-600 bg-clip-text text-transparent">
                  Advanced Property Filters
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Fine-tune your search to find exactly what you're looking for
                </p>
              </div>
              {draftFiltersCount > 0 && (
                <Badge
                  variant="default"
                  className="text-base px-4 py-1.5 bg-primary shadow-lg"
                >
                  {draftFiltersCount} Active
                </Badge>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-6">
            {/* Categorized Filters */}
            <div className="space-y-6">
              {/* Price & Value Filters */}
              <div>
                <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                  <span className="text-lg">💰</span>
                  Price & Value
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-6">
                  {draftFilters
                    .filter((f) =>
                      [
                        "assessedValueMin",
                        "assessedValueMax",
                        "assessedImprovementValueMin",
                        "assessedImprovementValueMax",
                        "assessedLandValueMin",
                        "assessedLandValueMax",
                        "belowMarketPrice",
                        "belowMarketPriceRateMin",
                        "belowMarketPriceRateMax",
                      ].includes(f.key)
                    )
                    .map((filter) => {
                      const { key, ...rest } = filter;
                      return (
                        <div key={key} className="space-y-2">
                          <Input {...rest} onChange={handleFilterInput(key)} />
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Property Features */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                  <span className="text-lg">🏠</span>
                  Property Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-6">
                  {draftFilters
                    .filter((f) =>
                      [
                        "bedroomsMin",
                        "bedroomsMax",
                        "bathroomsMin",
                        "bathroomsMax",
                        "attic",
                        "basement",
                        "pool",
                      ].includes(f.key)
                    )
                    .map((filter) => {
                      const { key, ...rest } = filter;
                      return (
                        <div key={key} className="space-y-2">
                          <Input {...rest} onChange={handleFilterInput(key)} />
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Status & Ownership */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                  <span className="text-lg">📋</span>
                  Status & Ownership
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-6">
                  {draftFilters
                    .filter((f) =>
                      [
                        "bankruptcy",
                        "bankruptcyRecordingDateMin",
                        "bankruptcyRecordingDateMax",
                        "businessOwner",
                        "cashBuyer",
                      ].includes(f.key)
                    )
                    .map((filter) => {
                      const { key, ...rest } = filter;
                      return (
                        <div key={key} className="space-y-2">
                          <Input {...rest} onChange={handleFilterInput(key)} />
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Auction & Dates */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                  <span className="text-lg">📅</span>
                  Auction & Important Dates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-6">
                  {draftFilters
                    .filter((f) =>
                      ["auctionDateMin", "auctionDateMax"].includes(f.key)
                    )
                    .map((filter) => {
                      const { key, ...rest } = filter;
                      return (
                        <div key={key} className="space-y-2">
                          <Input {...rest} onChange={handleFilterInput(key)} />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center pt-4 border-t gap-3 bg-muted/30 -m-6 mt-0 p-6">
            <Button
              variant="outline"
              onClick={handleClearDraftFilters}
              disabled={draftFiltersCount === 0}
              className="w-full sm:w-auto"
            >
              <X className="h-4 w-4 mr-2" />
              Clear All Filters
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={handleCancelFilters}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApplyFilters}
                className="flex-1 sm:flex-none bg-gradient-to-r from-primary via-orange-500 to-orange-600 hover:from-primary/90 hover:via-orange-500/90 hover:to-orange-600/90 text-white shadow-lg"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertySearch;
